/** Shared pricing math for PricingStep + PricingSidebar (keep in sync). */

export interface ItineraryTaxTotalsInput {
  netCostPaise: number;
  /**
   * Σ FLIGHT component costPaise (the ticket base fare). Excluded from the TCS
   * base - TCS applies to the flight MARGIN only, not the base fare. Optional/0
   * when the caller has no flight breakdown. Must mirror the server.
   */
  flightComponentPaise?: number;
  globalMarkupPaise: number;
  agentMarkupPaise: number;
  /** Absolute discount amount in paise (from Discount master % of final markup). */
  discountPaise: number;
  /**
   * Absolute upsell amount in paise (from Upsell master % of final markup).
   * Adds to the quote (cost add) — mutually exclusive with discount in the UI.
   */
  additionalDiscountPaise?: number;
  gstRatePercent: number;
  tcsRatePercent: number;
  applyTcs: boolean;
}

export interface ItineraryTaxTotals {
  effectiveAgentMarkupPaise: number;
  totalMarkupPaise: number;
  effectiveDiscountPaise: number;
  /** Upsell amount (cost add). Field name kept for API/DB compatibility. */
  effectiveAdditionalDiscountPaise: number;
  gstPaise: number;
  tcsPaise: number;
  taxablePaise: number;
  /** Customer-payable total, ROUNDED to a whole rupee (matches server + gateway). */
  grandTotalPaise: number;
  /** Exact total before the whole-rupee round (taxable + GST + TCS). */
  exactGrandTotalPaise: number;
  /** Signed rounding adjustment folded into grandTotalPaise (grand - exact). */
  roundOffPaise: number;
  /** Net cost + global (product) markup - the "package price" sales sees. */
  packagePricePaise: number;
}

/** Round a paise amount to the nearest whole rupee (nearest 100 paise), half up. */
export function roundToWholeRupeePaise(paise: number): number {
  if (paise <= 0) return 0;
  return Math.round(paise / 100) * 100;
}

export function computeItineraryTaxTotals(input: ItineraryTaxTotalsInput): ItineraryTaxTotals {
  // Markup is exactly what was set (system + agent adjustment). No minimum
  // floor - GST/TCS are computed on the real markup so this mirrors the server
  // (recompute-totals) and preview == what gets stored + charged.
  const totalMarkup = input.globalMarkupPaise + input.agentMarkupPaise;
  const effectiveAgentMarkup = totalMarkup - input.globalMarkupPaise;

  // Discount reduces the quote (capped to final markup). Upsell adds cost.
  // UI enforces mutual exclusivity; math still treats them independently.
  const effectiveDiscount = Math.min(Math.max(0, input.discountPaise), totalMarkup);
  const effectiveUpsell = Math.max(0, input.additionalDiscountPaise ?? 0);

  // GST is on markup after discount only (upsell is a cost add, not taxable markup).
  const gstBase = Math.max(0, totalMarkup - effectiveDiscount);
  const gstPaise = Math.round((gstBase * input.gstRatePercent) / 100);
  const taxablePaise = Math.max(
    0,
    input.netCostPaise + totalMarkup - effectiveDiscount + effectiveUpsell,
  );
  // TCS base INCLUDES gst (206C(1G) receipt basis) and EXCLUDES the flight base
  // fare (TCS on flight margin only; the flight markup stays in totalMarkup, so
  // it remains taxed). Must mirror the server (recompute-totals.ts) so preview
  // == stored == charged.
  const tcsBase = Math.max(0, taxablePaise + gstPaise - (input.flightComponentPaise ?? 0));
  const tcsPaise = input.applyTcs ? Math.round((tcsBase * input.tcsRatePercent) / 100) : 0;
  // Round the customer-payable total to a whole rupee (GST/TCS are percentages
  // and rarely land on one); the signed delta is surfaced as roundOffPaise so
  // the pricing/preview tab can show a "Round Off +/- Rs X" line. Mirrors the
  // server (recompute-totals) so preview == what gets stored + charged.
  const exactGrandTotalPaise = taxablePaise + gstPaise + tcsPaise;
  const grandTotalPaise = roundToWholeRupeePaise(exactGrandTotalPaise);
  const roundOffPaise = grandTotalPaise - exactGrandTotalPaise;

  return {
    effectiveAgentMarkupPaise: effectiveAgentMarkup,
    totalMarkupPaise: totalMarkup,
    effectiveDiscountPaise: effectiveDiscount,
    effectiveAdditionalDiscountPaise: effectiveUpsell,
    gstPaise,
    tcsPaise,
    taxablePaise,
    grandTotalPaise,
    exactGrandTotalPaise,
    roundOffPaise,
    packagePricePaise: input.netCostPaise + input.globalMarkupPaise,
  };
}

/** Amount = percent of final markup, in paise. */
export function discountAmountFromPercent(finalMarkupPaise: number, percent: number): number {
  if (finalMarkupPaise <= 0 || percent <= 0) return 0;
  return Math.round((finalMarkupPaise * percent) / 100);
}
